import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * Visible breadcrumb component + Schema.org `BreadcrumbList` JSON-LD.
 *
 * Why visible + structured: Google uses BreadcrumbList to render the
 * navigation chain in SERP (Home > Città > Ostuni) instead of the raw
 * URL — higher click-through rate. The visual breadcrumb also helps
 * mobile users understand site hierarchy.
 *
 * Usage:
 *   <Breadcrumb items={[
 *     { label: "Home", to: "/" },
 *     { label: "Città", to: "/" },
 *     { label: "Ostuni" }   // last item = current page, no `to`
 *   ]} />
 */
export const Breadcrumb = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <nav
      aria-label="Breadcrumb"
      data-testid="breadcrumb"
      className="text-[11px] uppercase tracking-widest text-[#6C706D] flex items-center flex-wrap gap-x-1 gap-y-1"
    >
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        return (
          <span key={i} className="inline-flex items-center gap-1">
            {item.to && !isLast ? (
              <Link
                to={item.to}
                className="hover:text-[#282C2A] transition-colors"
                data-testid={`breadcrumb-item-${i}`}
              >
                {item.label}
              </Link>
            ) : (
              <span
                className={isLast ? "text-[#282C2A]" : ""}
                data-testid={`breadcrumb-item-${i}`}
                aria-current={isLast ? "page" : undefined}
              >
                {item.label}
              </span>
            )}
            {!isLast && (
              <ChevronRight size={11} strokeWidth={1.5} className="text-[#A8A6A0]" />
            )}
          </span>
        );
      })}
    </nav>
  );
};

/**
 * Build a Schema.org BreadcrumbList JSON-LD object. Pass items in the
 * SAME shape as the Breadcrumb component above. Items without an absolute
 * URL are auto-resolved against `https://dealrating.it`.
 */
export const breadcrumbJsonLd = (items) => {
  if (!items || items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => {
      const el = {
        "@type": "ListItem",
        position: i + 1,
        name: item.label,
      };
      if (item.to) {
        const base = "https://dealrating.it";
        el.item = item.to.startsWith("http")
          ? item.to
          : `${base}${item.to.startsWith("/") ? item.to : "/" + item.to}`;
      }
      return el;
    }),
  };
};
